/**
 * Design system tokens — 8px grid.
 * Prefer these class strings over one-off px/py/radius values in modules.
 */
export const UI_SPACING_UNIT_PX = 8;

/** CSS variable names for code-driven surfaces such as charts and maps. */
export const cssToken = {
  surface: {
    canvas: "--surface-canvas",
    primary: "--surface-primary",
    secondary: "--surface-secondary",
    elevated: "--surface-elevated",
    inset: "--surface-inset",
    interactive: "--surface-interactive",
    interactiveHover: "--surface-interactive-hover",
    disabled: "--surface-disabled",
    card: "--surface-card",
    cardElevated: "--surface-card-elevated",
    cardHover: "--surface-card-hover",
    cardInset: "--surface-card-inset",
  },
  text: {
    primary: "--text-primary",
    secondary: "--text-secondary",
    muted: "--text-muted-ui",
    disabled: "--text-disabled",
    inverse: "--text-inverse",
  },
  border: {
    subtle: "--border-subtle",
    default: "--border-default",
    strong: "--border-strong-ui",
    interactive: "--border-interactive",
  },
  accent: {
    default: "--accent-primary",
    hover: "--accent-primary-hover",
    soft: "--accent-primary-soft",
    muted: "--accent-primary-muted",
    foreground: "--accent-foreground",
  },
  status: {
    success: "--semantic-success",
    warning: "--semantic-warning",
    error: "--semantic-error",
    info: "--semantic-info",
    offline: "--semantic-offline",
    maintenance: "--semantic-maintenance",
  },
  visualization: {
    grid: "--viz-grid",
    axis: "--viz-axis",
    tooltipBackground: "--viz-tooltip-bg",
    tooltipBorder: "--viz-tooltip-border",
    selection: "--viz-selection",
    thresholdWarning: "--viz-threshold-warning",
    thresholdCritical: "--viz-threshold-critical",
    series: [
      "--viz-series-1",
      "--viz-series-2",
      "--viz-series-3",
      "--viz-series-4",
      "--viz-series-5",
      "--viz-series-6",
      "--viz-series-7",
      "--viz-series-8",
    ],
  },
} as const;

export const uiRadius = {
  /** Primary panels, cards, modals */
  panel: "rounded-card",
  /** Inputs, buttons, compact controls */
  control: "rounded-control",
  /** Legacy alias */
  card: "rounded-card",
  shell: "rounded-shell",
  pill: "rounded-full",
} as const;

/** Standard control heights — all inputs/buttons should use h-10 (40px) unless icon-only */
export const uiControl = {
  height: "h-[var(--density-control-md)] min-h-[var(--density-control-md)]",
  heightSm: "h-[var(--density-control-sm)] min-h-[var(--density-control-sm)]",
  heightLg: "h-[var(--density-control-lg)] min-h-[var(--density-control-lg)]",
  padding: "px-ds-3 py-ds-2",
  paddingMd: "px-ds-4 py-ds-2.5",
  text: "text-caption font-medium",
} as const;

export const uiButton = {
  sm: "btn btn-sm min-h-[var(--density-control-sm)] px-ds-3 py-ds-2 text-caption",
  md: "btn min-h-[var(--density-control-md)] px-ds-4 py-ds-2.5 text-caption",
  lg: "btn min-h-[var(--density-control-lg)] px-ds-5 py-ds-3 text-body",
  icon: "btn btn-sm inline-flex h-[var(--density-control-sm)] w-[var(--density-control-sm)] min-h-[var(--density-control-sm)] min-w-[var(--density-control-sm)] shrink-0 !p-0",
} as const;

export const uiCard = {
  padding: "p-[var(--density-panel-padding)]",
  paddingSm: "p-[var(--density-panel-padding-compact)]",
  paddingLg: "p-ds-6",
  header: "px-ds-5 py-ds-4",
  sectionGap: "gap-ds-4",
  pageGap: "gap-ds-5",
} as const;

export const uiKpi = {
  value: "font-sans text-kpi tabular-nums text-foreground",
  label: "text-micro font-semibold uppercase tracking-[0.12em] text-muted",
  hint: "text-caption leading-relaxed text-muted",
} as const;

export const uiTabs = {
  /** Standard pill tabs (settings sub-nav, forms) */
  segmentList: "segment-tabs",
  segmentTrigger: "segment-tab",
  segmentTriggerActive: "segment-tab-active",
  /** Larger wizard step tabs */
  stepList: "flex gap-ds-2 overflow-x-auto rounded-card border border-card-border bg-card p-ds-2 shadow-card",
} as const;

export const uiDensity = {
  controlSm: "h-[var(--density-control-sm)]",
  control: "h-[var(--density-control-md)]",
  controlLg: "h-[var(--density-control-lg)]",
  rowCompact: "min-h-[var(--density-row-compact)]",
  rowComfortable: "min-h-[var(--density-row-comfortable)]",
} as const;

export const uiMotion = {
  fast: "duration-fast ease-standard",
  standard: "duration-base ease-standard",
  deliberate: "duration-slow ease-out",
} as const;

export const uiStatus = {
  success: "border-status-success-border bg-status-success-surface text-status-success",
  warning: "border-status-warning-border bg-status-warning-surface text-status-warning",
  error: "border-status-error-border bg-status-error-surface text-status-error",
  info: "border-status-info-border bg-status-info-surface text-status-info",
  offline: "border-status-offline-border bg-status-offline-surface text-status-offline",
  maintenance: "border-status-maintenance-border bg-status-maintenance-surface text-status-maintenance",
} as const;

export const uiPanelHeights = {
  empty: "min-h-[24rem]",
  emptyAuth: "min-h-[60vh]",
  chart: "h-[24rem]",
} as const;
